import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { http, CATEGORIES, fileUrl, imgUrl, apiError } from "@/lib/api";
import { AdminShell } from "./AdminShell";

const EMPTY = { title: "", excerpt: "", content: "", category: "sociedade", image: null, featured: false };
const field = "mt-1 w-full rounded-lg border bg-background px-3 py-2.5 outline-none focus:border-primary";

function ImageUpload({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { onChange((await http.post("/admin/upload", fd)).data.image); toast.success("Imagem carregada"); }
    catch (x) { toast.error(apiError(x)); } finally { setBusy(false); }
  };
  return (
    <label data-testid="post-image-upload" className="relative grid aspect-video cursor-pointer place-items-center overflow-hidden rounded-xl border-2 border-dashed bg-muted/50 hover:border-primary">
      {value ? <img src={fileUrl(value)} alt="" className="absolute inset-0 h-full w-full object-cover" /> : null}
      <span className="relative z-10 flex items-center gap-2 rounded-full bg-black/60 px-4 py-2 text-sm font-bold text-white">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}{value ? "Trocar imagem" : "Carregar imagem"}
      </span>
      <input data-testid="post-image-input" type="file" accept="image/*" onChange={pick} className="hidden" />
    </label>
  );
}

export default function PostEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setP({ ...p, [k]: e?.target ? e.target.value : e });
  useEffect(() => { if (id) http.get(`/admin/posts/${id}`).then((r) => setP({ ...EMPTY, ...r.data })); }, [id]);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    const body = { title: p.title, excerpt: p.excerpt, content: p.content, category: p.category, image: p.image, featured: p.featured };
    try {
      const { data } = id ? await http.put(`/admin/posts/${id}`, body) : await http.post("/admin/posts", body);
      toast.success(id ? "Publicação atualizada" : "Publicado! Já está no site.");
      navigate(id ? "/admin" : `/admin/editar/${data.id}`);
    } catch (x) { toast.error(apiError(x)); } finally { setBusy(false); }
  };
  return (
    <AdminShell>
      <Link to="/admin" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground"><ArrowLeft size={14} />Voltar</Link>
      <h1 className="mb-6 font-serif-display text-3xl font-black">{id ? "Editar publicação" : "Nova publicação"}</h1>
      <form onSubmit={save} className="grid gap-8 lg:grid-cols-3" data-testid="post-editor-form">
        <div className="space-y-5 lg:col-span-2">
          <div><label className="text-xs font-bold uppercase tracking-wider">Título</label><input data-testid="post-title-input" required value={p.title} onChange={set("title")} className={`${field} font-serif-display text-xl font-bold`} /></div>
          <div><label className="text-xs font-bold uppercase tracking-wider">Resumo (aparece no Google e na partilha)</label><textarea data-testid="post-excerpt-input" rows={2} value={p.excerpt} onChange={set("excerpt")} className={field} /></div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider">Conteúdo</label>
            <textarea data-testid="post-content-input" required rows={16} value={p.content} onChange={set("content")} className={`${field} font-reading leading-relaxed`} placeholder="Escreva a notícia. Separe os parágrafos com uma linha vazia. HTML também é aceite." />
          </div>
        </div>
        <div className="space-y-5">
          <ImageUpload value={p.image} onChange={set("image")} />
          <div>
            <label className="text-xs font-bold uppercase tracking-wider">Categoria</label>
            <select data-testid="post-category-select" value={p.category} onChange={set("category")} className={field}>
              {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
          <label className="flex items-center justify-between rounded-xl border bg-background p-4">
            <span><b className="block text-sm">Destaque no slide</b><span className="text-xs text-muted-foreground">Aparece no topo da página inicial</span></span>
            <Switch data-testid="post-featured-switch" checked={p.featured} onCheckedChange={set("featured")} />
          </label>
          <button data-testid="post-save-btn" disabled={busy} className="h-12 w-full rounded-full bg-primary font-bold text-white disabled:opacity-60">{busy ? "A guardar…" : id ? "Guardar alterações" : "Publicar agora"}</button>
          {id && (
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider">Imagem da marca (gerada)</p>
              <img data-testid="post-branded-preview" src={`${imgUrl(id, 640)}&t=${encodeURIComponent(p.title)}`} alt="" className="w-full rounded-lg" />
            </div>
          )}
        </div>
      </form>
    </AdminShell>
  );
}
